/** Tracks last Zoom media attach state to skip redundant SDK calls. */
export function createSessionVideoMediaRegistry() {
  const videoRegistry = {};
  let shareRegistry = {
    userId: null,
    targetEl: null,
    embedInPinPane: null,
    shareRenderType: null,
  };

  function shouldReattachVideo(userId, containerEl, isVideoOn) {
    const prev = videoRegistry[userId];
    if (!prev) {
      return !!containerEl;
    }
    return prev.containerEl !== containerEl || prev.isVideoOn !== isVideoOn;
  }

  function markVideoAttached(userId, containerEl, isVideoOn) {
    videoRegistry[userId] = { containerEl, isVideoOn };
  }

  function clearVideoRegistry(userId) {
    delete videoRegistry[userId];
  }

  function shouldRebindShare(shareUserId, targetEl, embedInPinPane, renderType) {
    if (!shareUserId || !targetEl) {
      return false;
    }
    return (
      shareRegistry.userId !== shareUserId ||
      shareRegistry.targetEl !== targetEl ||
      shareRegistry.embedInPinPane !== embedInPinPane ||
      shareRegistry.shareRenderType !== renderType
    );
  }

  function markShareBound(shareUserId, targetEl, embedInPinPane, renderType) {
    shareRegistry = {
      userId: shareUserId,
      targetEl,
      embedInPinPane,
      shareRenderType: renderType,
    };
  }

  function clearShareRegistry() {
    shareRegistry = {
      userId: null,
      targetEl: null,
      embedInPinPane: null,
      shareRenderType: null,
    };
  }

  function getShareTargetEl() {
    return shareRegistry.targetEl;
  }

  return {
    shouldReattachVideo,
    markVideoAttached,
    clearVideoRegistry,
    shouldRebindShare,
    markShareBound,
    clearShareRegistry,
    getShareTargetEl,
  };
}

/** Fingerprint of video-on state per participant — narrow effect dependency. */
export function getParticipantVideoFingerprint(users) {
  if (!Array.isArray(users)) {
    return '';
  }
  return users
    .map((u) => `${u.userId}:${!!(u.isVideoOn || u.bVideoOn)}`)
    .sort()
    .join('|');
}

/** Changes only on participant join/leave — not on mic/camera toggle. */
export function getParticipantIdsKey(users) {
  if (!Array.isArray(users)) {
    return '';
  }
  return users
    .map((u) => String(u.userId))
    .sort()
    .join('|');
}

const MEDIA_FIELDS = ['muted', 'isVideoOn', 'bVideoOn', 'sharerOn', 'displayName'];

function participantMediaSnapshot(user) {
  if (!user) {
    return null;
  }
  // Keep muted as true | false | undefined. Zoom reports undefined before audio
  // joins; !!undefined === !!false would drop the unmute transition that drives
  // multi-device mic exclusivity.
  return {
    userId: user.userId,
    muted: user.muted === true ? true : user.muted === false ? false : undefined,
    isVideoOn: !!(user.isVideoOn || user.bVideoOn),
    sharerOn: !!user.sharerOn,
    displayName: user.displayName || '',
  };
}

/** True when participant lists differ in media-relevant fields or membership. */
export function shouldUpdateParticipantsList(prev, next) {
  if (!Array.isArray(prev) || !Array.isArray(next)) {
    return true;
  }
  if (prev.length !== next.length) {
    return true;
  }
  const nextById = new Map(next.map((u) => [u.userId, u]));
  for (let i = 0; i < prev.length; i += 1) {
    const a = participantMediaSnapshot(prev[i]);
    const b = participantMediaSnapshot(nextById.get(prev[i].userId));
    if (!b || a.userId !== b.userId) {
      return true;
    }
    if (
      a.muted !== b.muted ||
      a.isVideoOn !== b.isVideoOn ||
      a.sharerOn !== b.sharerOn ||
      a.displayName !== b.displayName
    ) {
      return true;
    }
  }
  return false;
}

export function findSharerFromUsers(users) {
  if (!Array.isArray(users)) {
    return null;
  }
  return users.find((u) => u.sharerOn) || null;
}
